import classNames from 'classnames';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, DisplayS, HeadlineM } from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { glossary } from '@sorare/core/src/lib/glossary';
import { NBAClubs } from '@sorare/core/src/lib/nbaTeams';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { useNBAEvents } from '@sorare/gameplay/src/lib/events';

const Wrapper = styled(Vertical)`
  padding: var(--double-unit);
  position: relative;
  justify-content: space-between;

  @media ${laptopAndAbove} {
    overflow-y: auto;
    height: min(calc(100vh - var(--unit) * 8), 800px);
  }
`;

const TeamGrid = styled.ul`
  padding: 0;
  display: grid;
  grid-auto-rows: 150px;
  gap: var(--double-unit);
  grid-template-columns: repeat(2, minmax(100px, 1fr));

  @media ${tabletAndAbove} {
    grid-template-columns: repeat(3, minmax(100px, 1fr));
  }

  @media ${laptopAndAbove} {
    grid-template-columns: repeat(2, minmax(100px, 1fr));
  }
`;

const Team = styled.li`
  display: flex;
  cursor: pointer;
  gap: var(--unit);
  align-items: center;
  flex-direction: column;
  justify-content: center;
  padding: var(--unit);
  border-radius: var(--unit);
  background: rgba(var(--c-rgb-neutral-1000), 0.07);

  &.selected {
    outline: 2px solid var(--c-brand-600);
    background: rgba(var(--c-rgb-brand-600), 0.16);
  }
`;

const TeamLogo = styled(ResponsiveImg)`
  width: 64px;

  @media ${tabletAndAbove} {
    width: 80px;
  }
`;

const ButtonWrapper = styled.div`
  position: sticky;
  margin-top: auto;
  bottom: var(--double-unit);

  @media ${laptopAndAbove} {
    bottom: 0;
  }
`;

const StyledButton = styled(Button)`
  box-shadow: 0px 0px var(--double-unit) var(--c-neutral-100);
`;

type Props = {
  setStep: (step: number) => void;
  selectedFavoriteTeam?: string;
  setSelectedFavoriteTeam: (slug: string) => void;
};

export const SelectFavoriteTeam = ({
  setStep,
  selectedFavoriteTeam,
  setSelectedFavoriteTeam,
}: Props) => {
  const track = useNBAEvents();
  const sortedNBAClubs = [...NBAClubs].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  useEffect(() => {
    if (!selectedFavoriteTeam) {
      track('View Onboarding Pick favorite team');
    }
  }, [track, selectedFavoriteTeam]);

  return (
    <Wrapper gap={4}>
      <Vertical>
        <DisplayS brand className="text-center">
          <FormattedMessage
            id="S3Onboarding.SelectFavoriteTeam.title"
            defaultMessage="Pick a favorite team"
          />
        </DisplayS>
        <BodyM color="var(--c-neutral-700)" className="text-center">
          <FormattedMessage
            id="S3Onboarding.SelectFavoriteTeam.subtitle"
            defaultMessage="This will help us better personalize your Sorare NBA Experience."
          />
        </BodyM>
      </Vertical>
      <TeamGrid>
        {sortedNBAClubs.map(team => {
          const isSelected = selectedFavoriteTeam === team.slug;
          return (
            <Team
              role="button"
              key={team.slug}
              className={classNames('text-center', {
                selected: isSelected,
              })}
              onClick={() => {
                setSelectedFavoriteTeam(team.slug);
              }}
            >
              <TeamLogo src={team.pictureUrl} alt="" cropWidth={80} />
              <HeadlineM>{team.name}</HeadlineM>
            </Team>
          );
        })}
      </TeamGrid>
      <ButtonWrapper>
        <StyledButton
          fullWidth
          size="large"
          color="secondary"
          disabled={!selectedFavoriteTeam}
          onClick={() => {
            if (selectedFavoriteTeam) {
              track('Click Onboarding Pick favorite team', {
                team: selectedFavoriteTeam ?? '',
              });
              setStep(6);
            }
          }}
        >
          {!selectedFavoriteTeam ? (
            <FormattedMessage
              id="S3Onboarding.SelectFavoriteTeam.cta"
              defaultMessage="Pick a team"
            />
          ) : (
            <FormattedMessage {...glossary.confirm} />
          )}
        </StyledButton>
      </ButtonWrapper>
    </Wrapper>
  );
};
